/**
 * BaseFaultTreeView
 *
 * Abstract base class providing common functionality for all fault tree views.
 * Extend this class to create new view types with minimal boilerplate.
 */

/* eslint-disable @typescript-eslint/no-unused-vars */
import * as joint from '@joint/plus';
import {FaultTreeViewType} from '../../../@types/fault-tree-view-type';
import {
    FaultTreeBuildOptions, FaultTreeBuildResult, FaultTreeLayoutOptions, FaultTreeViewStrategy
} from './fault-tree-view-strategy';
import type {FaultTree} from '../../../@types/faultTree';
import type {FaultTreeNode} from '../../../@types/faultTreeNode';
import type EventSequence from '../../../@types/event-sequence';
// Note: buildESDGraphFromObject and toDendogram are imported lazily in the methods
// to avoid circular dependency with canvas-actions.ts

/**
 * Abstract base class for fault tree views.
 * Provides common utilities and default implementations that can be overridden.
 */
export abstract class BaseFaultTreeView implements FaultTreeViewStrategy {
    abstract readonly viewType: FaultTreeViewType;
    abstract readonly displayName: string;

    /**
     * Build the fault tree graph. Must be implemented by subclasses.
     */
    abstract buildGraph(
        eventSequence: EventSequence,
        faultTree: FaultTree | null,
        options: FaultTreeBuildOptions
    ): FaultTreeBuildResult;

    /**
     * Get the default layout options. Must be implemented by subclasses.
     */
    abstract getLayoutOptions(): FaultTreeLayoutOptions;

    /**
     * Create and configure the TreeLayout instance.
     * Can be overridden for custom layout behavior.
     */
    createLayout(
        graph: joint.dia.Graph,
        options?: Partial<FaultTreeLayoutOptions>
    ): joint.layout.TreeLayout {
        const layoutOptions = { ...this.getLayoutOptions(), ...options };
        return new joint.layout.TreeLayout({
            graph,
            direction: layoutOptions.direction,
            siblingGap: layoutOptions.siblingGap,
            parentGap: layoutOptions.parentGap,
        });
    }

    /**
     * Get layout options for FT expansion from an ESD event.
     * Default implementation uses standard vertical layout (direction: 'B').
     * Can be overridden for view-specific behavior.
     *
     * @param isInitiatingEvent Whether the event being expanded is the Initiating Event
     * @returns Layout options for the expanded fault tree
     */
    getFtExpansionLayoutOptions(isInitiatingEvent: boolean): FaultTreeLayoutOptions {
        // Standard view uses vertical layout for all events
        return {
            direction: 'B',
            siblingGap: 50,
            parentGap: 150,
        };
    }

    /**
     * Position the FT root node relative to the clicked ESD event.
     * Default implementation positions the FT below the ESD graph.
     * Can be overridden for view-specific behavior.
     *
     * @param ftRoot The root element of the expanded fault tree
     * @param clickedElement The ESD event element that was expanded
     * @param graph The graph containing the ESD
     */
    positionFtRoot(
        ftRoot: joint.dia.Element,
        clickedElement: joint.dia.Element,
        graph: joint.dia.Graph
    ): void {
        // Standard view: position FT below the ESD graph
        const [source] = graph.getSources();
        let maxy = 300;

        graph.getSuccessors(source).forEach((cell) => {
            if (cell.get('nodeClass') !== 'FAULTTREENODE') {
                if (cell.position().y > maxy) {
                    maxy = cell.position().y;
                }
            }
        });

        const yoffset = maxy > 300 ? maxy : 300;
        ftRoot.position(clickedElement.position().x, yoffset + 250);
    }

    /**
     * Build the ESD (Event Sequence Diagram) graph.
     * Default implementation uses buildESDGraphFromObject.
     * Can be overridden for view-specific behavior.
     *
     * @param eventSequence The event sequence data
     * @param graph The graph to populate with ESD nodes
     */
    buildEsdGraph(
        eventSequence: EventSequence,
        graph: joint.dia.Graph
    ): void {
        // Lazy import to avoid circular dependency
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const { buildESDGraphFromObject } = require('../canvas-actions');
        buildESDGraphFromObject(eventSequence, graph);
    }

    /**
     * Get layout options for the ESD graph.
     * Default implementation returns standard horizontal layout.
     * Can be overridden for view-specific behavior.
     *
     * @returns Layout options for the ESD TreeLayout
     */
    getEsdLayoutOptions(): FaultTreeLayoutOptions {
        return {
            direction: 'R',
            siblingGap: 50,
            parentGap: 100,
        };
    }

    /**
     * Apply any post-processing to the ESD graph after layout.
     * Default implementation converts to dendogram style.
     * Can be overridden for view-specific behavior.
     *
     * @param graph The graph containing the ESD
     * @param source The source/root element of the ESD
     */
    applyEsdPostProcessing(
        graph: joint.dia.Graph,
        source: joint.dia.Element
    ): void {
        // Lazy import to avoid circular dependency
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const { toDendogram } = require('../canvas-actions');
        toDendogram(graph, source);
    }

    // ==================== Helper Methods ====================

    /**
     * Calculate the depth of each node in the tree via BFS from the root.
     *
     * @param rootId The ID of the root node
     * @param ftNodeById Map of node ID to node data
     * @returns Map of node ID to depth (0 = root)
     */
    protected calculateNodeDepths(
        rootId: number,
        ftNodeById: Map<number, FaultTreeNode>
    ): Map<number, number> {
        const depths = new Map<number, number>();

        if (!ftNodeById.has(rootId)) {
            return depths;
        }

        const queue: Array<{ id: number; depth: number }> = [{ id: rootId, depth: 0 }];

        while (queue.length > 0) {
            const { id, depth } = queue.shift()!;
            if (depths.has(id)) continue;

            depths.set(id, depth);

            const node = ftNodeById.get(id);
            if (node?.childIds) {
                for (const childId of node.childIds) {
                    if (!depths.has(childId)) {
                        queue.push({ id: childId, depth: depth + 1 });
                    }
                }
            }
        }

        return depths;
    }

    /**
     * Determine which nodes should be loaded based on depth and expanded state.
     *
     * @param faultTreeNodes All nodes in the fault tree
     * @param ftNodeById Map of node ID to node data
     * @param depthMap Map of node ID to depth
     * @param maxDepth Maximum depth to load automatically
     * @param expandedNodeIds Set of node IDs explicitly expanded by user
     * @returns Set of node IDs that should be loaded
     */
    protected determineNodesToLoad(
        faultTreeNodes: FaultTreeNode[],
        ftNodeById: Map<number, FaultTreeNode>,
        depthMap: Map<number, number>,
        maxDepth: number,
        expandedNodeIds: Set<number>
    ): Set<number> {
        const nodesToLoad = new Set<number>();

        // First pass: load all nodes within maxDepth
        for (const ftNode of faultTreeNodes) {
            const depth = depthMap.get(ftNode.id) ?? 0;
            if (depth <= maxDepth) {
                nodesToLoad.add(ftNode.id);
            }
        }

        // Second pass: for each expanded node, add path from root and children
        for (const expandedId of expandedNodeIds) {
            const expandedNode = ftNodeById.get(expandedId);
            if (!expandedNode) continue;

            // Add the expanded node itself
            nodesToLoad.add(expandedId);

            // Add its children
            if (expandedNode.childIds) {
                for (const childId of expandedNode.childIds) {
                    nodesToLoad.add(childId);
                }
            }

            // Add all ancestors (path from root to this node)
            let currentId: number | undefined = expandedNode.parentId;
            while (currentId !== undefined) {
                nodesToLoad.add(currentId);
                const parentNode = ftNodeById.get(currentId);
                currentId = parentNode?.parentId;
            }
        }

        return nodesToLoad;
    }

    /**
     * Check if a node has children that are not being loaded.
     *
     * @param ftNode The node to check
     * @param nodesToLoad Set of node IDs being loaded
     * @returns true if the node has unloaded children
     */
    protected hasUnloadedChildren(
        ftNode: FaultTreeNode,
        nodesToLoad: Set<number>
    ): boolean {
        if (!ftNode.childIds || ftNode.childIds.length === 0) {
            return false;
        }
        return ftNode.childIds.some(childId => !nodesToLoad.has(childId));
    }

    /**
     * Build a lookup map from an array of fault tree nodes.
     *
     * @param faultTreeNodes Array of fault tree nodes
     * @returns Map of node ID to node
     */
    protected buildNodeLookupMap(faultTreeNodes: FaultTreeNode[]): Map<number, FaultTreeNode> {
        const map = new Map<number, FaultTreeNode>();
        for (const node of faultTreeNodes) {
            map.set(node.id, node);
        }
        return map;
    }

    /**
     * Clean stale IDs from the expanded nodes set.
     * Removes IDs of nodes that no longer exist.
     *
     * @param expandedNodeIds Set to clean
     * @param ftNodeById Map of valid node IDs
     */
    protected cleanStaleExpandedIds(
        expandedNodeIds: Set<number>,
        ftNodeById: Map<number, FaultTreeNode>
    ): void {
        const staleIds: number[] = [];
        for (const expandedId of expandedNodeIds) {
            if (!ftNodeById.has(expandedId)) {
                staleIds.push(expandedId);
            }
        }
        for (const staleId of staleIds) {
            expandedNodeIds.delete(staleId);
        }
    }
}
